import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Skeleton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Person, Email, Edit, Save, Cancel } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useUsers from "../hooks/useUsers/useUsers";
import Avatar from "../profile/Avatar";
import ImageUploader from "../profile/ImageUploader";
import Modal from "@mui/material/Modal";
import { User, UserPreferences } from "../types/user";

const Profile = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const { getUser, updateUsername, updateEmailPreferences } = useUsers();

  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Username editing state
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  // Add state for avatar editing and modal
  const [avatarEditOpen, setAvatarEditOpen] = useState(false);
  const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);

  // User preferences state
  const [updatingPreferences, setUpdatingPreferences] = useState(false);

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isLoading && isAuthenticated && user?.email) {
        try {
          setLoading(true);
          setError(null);

          const userData = await getUser(user.email);
          if (userData) {
            setProfile({
              displayName:
                userData.displayName || user.nickname || user.name || "User",
              email: user.email,
              preferences: userData.preferences,
              auth0Picture: userData.auth0Picture,
            });
          } else {
            setError("Failed to load user profile");
          }
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProfile();
  }, [isLoading, isAuthenticated, user, getUser]);

  const handleEditClick = () => {
    setNewUsername(profile?.displayName || "");
    setUsernameError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewUsername("");
    setUsernameError(null);
  };

  const handleSaveUsername = async () => {
    if (!profile || !user) return;

    // Validate username
    if (!newUsername.trim()) {
      setUsernameError("Username cannot be empty");
      return;
    }

    if (newUsername.trim().length < 2) {
      setUsernameError("Username must be at least 2 characters long");
      return;
    }

    if (newUsername.trim().length > 50) {
      setUsernameError("Username must be less than 50 characters");
      return;
    }

    try {
      setUpdating(true);
      setUsernameError(null);

      const response = await updateUsername(user.email || "", newUsername);
      if (response instanceof Error) {
        throw new Error(`Failed to update username: ${response.message}`);
      }
      // Update local state
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              displayName: newUsername.trim(),
            }
          : null
      );

      setSuccess("Username updated successfully!");
      setIsEditing(false);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setUsernameError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Add handler for avatar upload success
  const handleAvatarUploadSuccess = () => {
    setAvatarEditOpen(false);
    setAvatarRefreshKey((k) => k + 1); // force Avatar to reload
    setSuccess("Profile picture updated successfully!");
    setTimeout(() => setSuccess(null), 3000);
  };

  // Handler for updating email preferences
  const handleEmailPreferenceChange = async (
    preference: string,
    value: boolean
  ) => {
    if (!user?.email) return;

    setUpdatingPreferences(true);

    const response = await updateEmailPreferences(user.email, {
      [preference]: value,
    });
    if (response instanceof Error) {
      setError(`Failed to update email preferences: ${response.message}`);
      setTimeout(() => setError(null), 5000);
    } else {
      if (!profile) return;

      // Update local state
      const updatedProfile = profile;
      if (!updatedProfile?.preferences) {
        updatedProfile.preferences = { weekReadyNotification: false };
      }
      updatedProfile.preferences[preference as keyof UserPreferences] =
        value as boolean;
      setProfile(updatedProfile);

      setSuccess("Email preferences updated successfully!");
      setTimeout(() => setSuccess(null), 3000);
    }

    setUpdatingPreferences(false);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please sign in to view your profile.</Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error loading profile: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Profile Information Card */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Person color="primary" />
              Profile Information
            </Typography>

            <Stack spacing={2}>
              {/* Avatar and Basic Info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ position: "relative", width: 64, height: 64 }}>
                  <Avatar
                    key={avatarRefreshKey}
                    email={profile?.email}
                    auth0Picture={user?.picture}
                    alt={profile?.displayName || "User"}
                    sx={{ width: 64, height: 64 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      minWidth: 0,
                      p: 0.5,
                      zIndex: 2,
                      background: "white",
                      borderRadius: "50%",
                      boxShadow: 1,
                      transform: "translate(35%,-35%)",
                    }}
                    onClick={() => setAvatarEditOpen(true)}
                    aria-label="Edit profile picture"
                  >
                    <Edit fontSize="small" />
                  </Button>
                </Box>
                <Box>
                  <Typography variant="h6">
                    {loading ? (
                      <Skeleton
                        animation="wave"
                        variant="text"
                        width={120}
                        height={36}
                      />
                    ) : (
                      profile?.displayName || "No display name set"
                    )}
                  </Typography>
                </Box>
              </Box>
              <Modal
                open={avatarEditOpen}
                onClose={() => setAvatarEditOpen(false)}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    minWidth: 350,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Edit Profile Picture
                  </Typography>
                  {profile?.email && (
                    <ImageUploader
                      email={profile.email}
                      onSuccess={handleAvatarUploadSuccess}
                      onCancel={() => setAvatarEditOpen(false)}
                    />
                  )}
                  <Button
                    onClick={() => setAvatarEditOpen(false)}
                    sx={{ mt: 2 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Modal>

              <Divider />

              {/* Email */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Email color="action" />
                <Typography variant="body1">
                  {profile?.email || user?.email || "No email available"}
                </Typography>
              </Box>

              <Divider />

              {/* Username Section */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    Username
                  </Typography>
                  {!isEditing && (
                    <Button
                      startIcon={<Edit />}
                      onClick={handleEditClick}
                      size="small"
                      variant="outlined"
                    >
                      Edit
                    </Button>
                  )}
                </Box>

                {isEditing ? (
                  <Stack spacing={2}>
                    <TextField
                      value={newUsername}
                      onChange={(
                        e: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >
                      ) => setNewUsername(e.target.value)}
                      label="Username"
                      variant="outlined"
                      fullWidth
                      error={!!usernameError}
                      helperText={usernameError}
                      disabled={updating}
                    />
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        startIcon={<Save />}
                        onClick={handleSaveUsername}
                        variant="contained"
                        disabled={updating}
                      >
                        {updating ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        startIcon={<Cancel />}
                        onClick={handleCancelEdit}
                        variant="outlined"
                        disabled={updating}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    {loading ? (
                      <Skeleton
                        animation="wave"
                        variant="text"
                        width={120}
                        height={24}
                      />
                    ) : (
                      profile?.displayName || "No username set"
                    )}
                  </Typography>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Email Preferences Card */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Email color="primary" />
              Email Preferences
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={profile?.preferences?.weekReadyNotification}
                    onChange={(e) =>
                      handleEmailPreferenceChange(
                        "weekReadyNotification",
                        e.target.checked
                      )
                    }
                    disabled={updatingPreferences}
                  />
                }
                label="Receive notification when week is ready"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "0.95rem",
                  },
                }}
              />

              {updatingPreferences && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Updating preferences...
                  </Typography>
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default Profile;
